import { Alert, Divider, Paper, SvgIcon, Typography } from "@mui/material";
import { classes } from "portal/theme/theme";
import { DailyMetricResponse, MetricDataSource } from "protos/portal/metrics";
import { DATE_PATH_FORMAT } from "portal/utils/dates";
import { DateTime } from "luxon";
import { EntryTimingGradeRow } from "./EntryTimingGradeRow";
import {
  FeatureFlag,
  useFeatureFlagForCustomer,
} from "portal/utils/hooks/useFeatureFlag";
import {
  getMetricName,
  METRIC_ACRES_WEEDED,
  METRIC_AVERAGE_CROP_SIZE,
  METRIC_AVERAGE_SPEED,
  METRIC_AVERAGE_TARGETABLE_REQ_LASER_TIME,
  METRIC_AVERAGE_WEED_SIZE,
  METRIC_BANDING_CONFIG,
  METRIC_BANDING_PROFILES,
  METRIC_COVERAGE_SPEED,
  METRIC_CROP,
  METRIC_CROP_DENSITY,
  METRIC_CROPS,
  METRIC_FRACTION_BROADLEAF,
  METRIC_FRACTION_GRASS,
  METRIC_FRACTION_OFFSHOOT,
  METRIC_FRACTION_PURSLANE,
  METRIC_JOB_NAME,
  METRIC_JOBS,
  METRIC_KEPT_CROPS,
  METRIC_KILLED_WEEDS,
  METRIC_MISSED_CROPS,
  METRIC_MISSED_WEEDS,
  METRIC_OPERATOR_EFFECTIVENESS,
  METRIC_OVERALL_EFFICIENCY,
  METRIC_SKIPPED_CROPS,
  METRIC_SKIPPED_WEEDS,
  METRIC_THINNED_CROPS,
  METRIC_THINNING_EFFICIENCY,
  METRIC_TIME_EFFICIENCY,
  METRIC_TOTAL_WEEDS,
  METRIC_TOTAL_WEEDS_IN_BAND,
  METRIC_UPTIME,
  METRIC_VALID_CROPS,
  METRIC_WEED_DENSITY,
  METRIC_WEEDING_EFFICIENCY,
  METRIC_WEEDING_TIME,
} from "portal/utils/certifiedMetrics";
import { isEmpty } from "portal/utils/objects";
import { JigsawCropMetrics } from "./certifiedMetrics/JigsawCropMetrics";
import { JigsawWeedFractions } from "./certifiedMetrics/JigsawWeedFractions";
import { MetricHelp } from "portal/components/MetricHelp";
import { MetricList, MetricNumber, MetricString } from "portal/utils/metrics";
import { MetricMeasurement } from "./measurement/MetricMeasurement";
import {
  MetricRow,
  MetricSubsection,
} from "./certifiedMetrics/MetricSubsection";
import { PlantProfileMetricsSection } from "./certifiedMetrics/PlantProfileMetricsSection";
import { useJigsawMetrics } from "portal/utils/hooks/useJigsawMetrics";
import { useTranslation } from "react-i18next";
import { ViewPlaceholder } from "./ViewPlaceholder";
import Grid from "@mui/material/Grid";
import JobStatusIcon from "@mui/icons-material/Circle";
import React, { FunctionComponent, PropsWithChildren } from "react";

interface CertifiedMetricsProps {
  // If set (for job metrics), show a "Job Status: {Active/Inactive}"
  // pseudometric. Otherwise (for daily metrics), show the job name.
  job?: JobData | undefined;
  className?: string;
  customerUuid?: string;
  metrics: DailyMetricResponse;
}

interface JobData {
  active: boolean;
}

export const CertifiedMetrics: FunctionComponent<CertifiedMetricsProps> = ({
  className,
  customerUuid,
  job,
  metrics,
}) => {
  const { t } = useTranslation();
  const useJigsaw = useJigsawMetrics();
  const { isEnabled: entryTimingEnabled } = useFeatureFlagForCustomer(
    FeatureFlag.ENTRY_TIMING,
    customerUuid
  );
  const showEntryTiming = job !== undefined && entryTimingEnabled === true;

  const today = DateTime.local().toFormat(DATE_PATH_FORMAT);

  if (isEmpty(metrics)) {
    return (
      <ViewPlaceholder
        text={t("views.fleet.robots.history.errors.noMetrics")}
      />
    );
  }

  return (
    <div className={className}>
      {metrics.date === today && (
        <Alert className="mb-8 self-stretch" severity="warning">
          {t("views.fleet.robots.history.warnings.ongoing")}
        </Alert>
      )}

      <MetricSection className="mb-5">
        <Typography
          variant="h3"
          className="font-poppins normal-case text-[24px] font-bold mb-5"
        >
          {t("views.metrics.sections.performanceAndMachineStats")}
        </Typography>

        <Grid container className="w-full items-stretch" spacing={2}>
          <HeadlineMetric metric={METRIC_OVERALL_EFFICIENCY} data={metrics} />
          <HeadlineMetric metric={METRIC_COVERAGE_SPEED} data={metrics} />
          <HeadlineMetric metric={METRIC_TIME_EFFICIENCY} data={metrics} />
          <HeadlineMetric metric={METRIC_WEEDING_TIME} data={metrics} />
        </Grid>

        <Grid container spacing={2} className="w-full mt-2">
          <Grid
            className="flex flex-col gap-3 lg:pr-12"
            size={{ xs: 12, lg: 6 }}
          >
            {job !== undefined && <JobStatus isJobActive={job.active} />}
            {metrics.dataSource === MetricDataSource.CLASSIC ? (
              <InlineMetric metric={METRIC_CROP} data={metrics} />
            ) : (
              <ListMetric
                metric={METRIC_CROPS}
                values={metrics.crops.map(({ id, localizedName }) => ({
                  id,
                  name: localizedName,
                }))}
              />
            )}
            {metrics.jobs.length > 0 ? (
              <ListMetric metric={METRIC_JOBS} values={metrics.jobs} />
            ) : job === undefined ? (
              <InlineMetric metric={METRIC_JOB_NAME} data={metrics} />
            ) : undefined}
            <InlineMetric metric={METRIC_ACRES_WEEDED} data={metrics} />
            <InlineMetric metric={METRIC_WEEDING_EFFICIENCY} data={metrics} />
            <InlineMetric metric={METRIC_THINNING_EFFICIENCY} data={metrics} />
          </Grid>
          <Grid
            className="flex flex-col gap-3 lg:pr-12"
            size={{ xs: 12, lg: 6 }}
          >
            <InlineMetric metric={METRIC_UPTIME} data={metrics} />
            <InlineMetric metric={METRIC_AVERAGE_SPEED} data={metrics} />
            <InlineMetric
              metric={METRIC_AVERAGE_TARGETABLE_REQ_LASER_TIME}
              data={metrics}
            />
            <InlineMetric
              metric={METRIC_OPERATOR_EFFECTIVENESS}
              data={metrics}
            />
            {metrics.bandingProfiles.length > 0 ? (
              <ListMetric
                metric={METRIC_BANDING_PROFILES}
                values={metrics.bandingProfiles}
              />
            ) : (
              <InlineMetric metric={METRIC_BANDING_CONFIG} data={metrics} />
            )}
          </Grid>
        </Grid>
      </MetricSection>

      <MetricSection className="border-0">
        <div className="mb-5 flex flex-col gap-1.5">
          <Typography
            variant="h3"
            className="font-poppins normal-case text-[24px] font-bold"
          >
            {t("views.metrics.sections.estimatedFieldMetrics")}
          </Typography>
          <Typography className="text-xs opacity-50 italic">
            {t("views.metrics.sections.estimatedFieldMetricsDisclaimer")}
          </Typography>
        </div>

        {useJigsaw &&
        metrics.dataSource === MetricDataSource.JIGSAW &&
        (metrics.plantProfileMetrics.length > 1 ||
          (metrics.plantProfileMetrics.length > 0 &&
            metrics.plantProfileMetrics[0]?.profileId)) ? (
          <div className="flex flex-col gap-3">
            {metrics.plantProfileMetrics.map((group) => (
              <PlantProfileMetricsSection
                key={
                  group.unattributed
                    ? "unattributed"
                    : group.profileId || "none"
                }
                group={group}
                showEntryTiming={showEntryTiming}
              />
            ))}
          </div>
        ) : (
          <Grid container spacing={2}>
            <Grid
              className="flex flex-col gap-3 lg:pr-12"
              size={{ xs: 12, lg: 6 }}
            >
              <InlineMetric metric={METRIC_WEED_DENSITY} data={metrics} />
              <InlineMetric metric={METRIC_AVERAGE_WEED_SIZE} data={metrics} />
              {showEntryTiming ? (
                <EntryTimingGradeRow grade={metrics.entryTimingGrade} />
              ) : undefined}
              <MetricSubsection>
                <InlineMetric metric={METRIC_TOTAL_WEEDS} data={metrics} />
                <InlineMetric
                  metric={METRIC_TOTAL_WEEDS_IN_BAND}
                  data={metrics}
                />
                <Divider className="w-full my-2" />
                <InlineMetric metric={METRIC_KILLED_WEEDS} data={metrics} />
                <InlineMetric metric={METRIC_MISSED_WEEDS} data={metrics} />
                <InlineMetric metric={METRIC_SKIPPED_WEEDS} data={metrics} />
              </MetricSubsection>
              {useJigsaw ? (
                <JigsawWeedFractions
                  weedMetricsByCategory={
                    // The single group here (jigsaw single-profile, or the
                    // sentinel group legacy back-fill emits); the top-level
                    // map covers older cached responses without groups.
                    metrics.plantProfileMetrics[0]?.weedMetricsByCategory ??
                    metrics.weedMetricsByCategory
                  }
                />
              ) : (
                <MetricSubsection>
                  <InlineMetric
                    metric={METRIC_FRACTION_BROADLEAF}
                    data={metrics}
                  />
                  <InlineMetric
                    metric={METRIC_FRACTION_PURSLANE}
                    data={metrics}
                  />
                  <InlineMetric
                    metric={METRIC_FRACTION_OFFSHOOT}
                    data={metrics}
                  />
                  <InlineMetric metric={METRIC_FRACTION_GRASS} data={metrics} />
                </MetricSubsection>
              )}
            </Grid>
            <Grid
              className="flex flex-col gap-3 lg:pr-12"
              size={{ xs: 12, lg: 6 }}
            >
              {/* deliberate: legacy-sourced metrics have no real per-crop
                breakdown, so even with jigsaw enabled we show the aggregate
                crop metrics rather than pretend */}
              {useJigsaw && metrics.dataSource === MetricDataSource.JIGSAW ? (
                <JigsawCropMetrics
                  cropMetricsByCategory={
                    metrics.plantProfileMetrics[0]?.cropMetricsByCategory ??
                    metrics.cropMetricsByCategory
                  }
                />
              ) : (
                <>
                  <InlineMetric metric={METRIC_CROP_DENSITY} data={metrics} />
                  <InlineMetric
                    metric={METRIC_AVERAGE_CROP_SIZE}
                    data={metrics}
                  />
                  <MetricSubsection>
                    <InlineMetric metric={METRIC_VALID_CROPS} data={metrics} />
                    <InlineMetric
                      metric={METRIC_THINNED_CROPS}
                      data={metrics}
                    />
                    <InlineMetric
                      metric={METRIC_SKIPPED_CROPS}
                      data={metrics}
                    />
                    <InlineMetric metric={METRIC_MISSED_CROPS} data={metrics} />
                    <InlineMetric metric={METRIC_KEPT_CROPS} data={metrics} />
                  </MetricSubsection>
                </>
              )}
            </Grid>
          </Grid>
        )}
      </MetricSection>
    </div>
  );
};

interface MetricSectionProps {
  className?: string;
}

const MetricSection: FunctionComponent<
  PropsWithChildren<MetricSectionProps>
> = ({ className, children }) => {
  return (
    <Paper
      variant="outlined"
      className={classes("w-full px-8 py-7 border-1 bg-transparent", className)}
    >
      {children}
    </Paper>
  );
};

interface HeadlineMetricProps {
  metric: MetricNumber;
  data: DailyMetricResponse;
}

const HeadlineMetric: FunctionComponent<HeadlineMetricProps> = ({
  metric,
  data,
}) => {
  const { t } = useTranslation();

  return (
    <Grid size={{ xs: 12, lg: 3 }}>
      <Paper
        variant="outlined"
        className="h-full pt-6 pb-4 px-5 border-1 flex flex-col gap-2 "
      >
        <Typography className="font-bold text-[20px]">
          <div className="flex justify-start">
            <MetricMeasurement data={data} metric={metric} />
          </div>
        </Typography>
        <Typography className="text-sm" style={{ overflowWrap: "anywhere" }}>
          {getMetricName(t, metric)}
          <MetricHelp metric={metric} className="ms-1 align-middle " />
        </Typography>
      </Paper>
    </Grid>
  );
};

interface JobStatusProps {
  isJobActive: boolean;
}

const JobStatus: FunctionComponent<JobStatusProps> = ({ isJobActive }) => {
  const { t } = useTranslation();

  return (
    <MetricRow>
      <span className="font-medium">
        {t("views.metrics.jobStatus.description")}
      </span>
      <span className="inline-flex items-center gap-1">
        <SvgIcon
          color={isJobActive ? "success" : "inherit"}
          className="h-3 w-3 shrink-0"
        >
          <JobStatusIcon />
        </SvgIcon>
        {isJobActive
          ? t("utils.descriptors.active")
          : t("utils.descriptors.inactive")}
      </span>
    </MetricRow>
  );
};

interface InlineMetricProps {
  metric: MetricNumber | MetricString;
  data: DailyMetricResponse;
}

const InlineMetric: FunctionComponent<InlineMetricProps> = ({
  metric,
  data,
}) => {
  const { t } = useTranslation();

  return (
    <MetricRow>
      <span className="font-medium">
        {getMetricName(t, metric)}
        <MetricHelp metric={metric} className="ms-1 align-middle" />
      </span>
      <MetricMeasurement data={data} metric={metric} />
    </MetricRow>
  );
};

const ListMetric: FunctionComponent<{
  metric: MetricList;
  values: { id: string; name: React.ReactNode }[];
}> = ({ metric, values }) => {
  const { t } = useTranslation();

  return (
    <MetricRow>
      <span className="font-medium">
        {getMetricName(t, metric)}
        <MetricHelp metric={metric} className="ms-1 align-middle" />
      </span>
      {/* w-full: always wrap the list onto its own line below the label. */}
      <ul className="m-0 pl-4 w-full">
        {values.map((value) => (
          <li key={value.id}>{value.name}</li>
        ))}
      </ul>
    </MetricRow>
  );
};
